import store from '@/store';
import axios from 'axios';

// Last check timestamp to prevent too frequent checks
let lastValidationCheck = 0;
const CHECK_THRESHOLD_MS = 5000; // Don't check more frequently than every 5 seconds

/**
 * Validates the authentication token and handles expired tokens.
 * This can be called from any component before making API calls.
 * @returns {Promise<boolean>} Returns true if token is valid, false otherwise
 */
export async function validateToken() {
    const currentTime = Date.now();

    // Skip validation if checked recently to avoid excessive API calls
    if (currentTime - lastValidationCheck < CHECK_THRESHOLD_MS) {
        return !!localStorage.getItem('bearer_token');
    }

    const bearerToken = localStorage.getItem('bearer_token');
    if (!bearerToken) {
        handleInvalidToken();
        return false;
    }

    // Set timestamp even before successful validation
    lastValidationCheck = currentTime;

    try {
        // Make a lightweight request to validate the token
        await axios.get(`${import.meta.env.VITE_BASE_URL}/user`, {
            headers: {
                'Authorization': `Bearer ${bearerToken}`
            }
        });
        return true;
    } catch (error) {
        if (error.response && error.response.status === 401) {
            handleInvalidToken();
            return false;
        }
        return !!bearerToken; // Consider token valid if error is not auth-related
    }
}

/**
 * Handle token expiration and cleanup
 */
function handleInvalidToken() {
    try {
        // Display a warning message to the user
        if (store && store.dispatch) {
            store.dispatch('handleExpiredSession');
        }
    } catch (error) {
        setTimeout(() => { window.location.href = 'https://vlifemedia.com'; }, 2000);
    }
}

/**
 * Add validation to DOM events
 */
export function setupGlobalTokenValidation() {
    if (typeof window !== 'undefined') {
        // Add event listener to all clicks
        document.addEventListener('click', async (e) => {
            // Check if the click is on a button, input, or actionable element
            const isActionElement = e.target.tagName === 'BUTTON' ||
                                    e.target.tagName === 'INPUT' ||
                                    e.target.tagName === 'A' ||
                                    e.target.closest('button') ||
                                    e.target.closest('.switch-container') ||
                                    e.target.closest('a[href]');
                                    
            if (isActionElement && store.state.isAuthenticated) {
                // Validate the token on user interaction but don't block the interaction
                validateToken();
            }
        }, true);

        // Also check on form submissions
        document.addEventListener('submit', async (e) => {
            if (store.state.isAuthenticated) {
                validateToken();
            }
        }, true);
    }
}

// Export a default object with all functions
export default {
    validateToken,
    setupGlobalTokenValidation
};